import {
  AppModal,
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import {
  FilterSelector,
  type FilterOption,
} from "@/components/ui/FilterSelector/FilterSelector";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import { cn } from "@/lib/utils/cn";
import { ArrowUpDown, Check, SlidersHorizontal, X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

export type TaskSortOption =
  | "createdDateAsc"
  | "createdDateDesc"
  | "dueDateAsc"
  | "dueDateDesc"
  | "priorityAsc"
  | "priorityDesc";

const taskSortOptions: Array<{ label: string; value: TaskSortOption }> = [
  { label: "Created date (newest first)", value: "createdDateDesc" },
  { label: "Created date (oldest first)", value: "createdDateAsc" },
  { label: "Due date (soonest first)", value: "dueDateAsc" },
  { label: "Due date (latest first)", value: "dueDateDesc" },
  { label: "Priority (highest first)", value: "priorityDesc" },
  { label: "Priority (lowest first)", value: "priorityAsc" },
];

function QuickFilter({
  filterOption,
  selectedValues,
  onAddFilter,
  onRemoveFilter,
}: {
  filterOption: FilterOption;
  selectedValues: string[];
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const input = filterOption.input;
  const items =
    input.type === "select" || input.type === "searchable-select"
      ? input.items
      : [];
  const asyncInput =
    input.type === "searchable-select" && input.onSearchChange
      ? input
      : undefined;
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    asyncInput?.onOpenChange?.(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-full px-3 text-xs font-medium"
        >
          {filterOption.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command shouldFilter={!asyncInput}>
          {input.type === "searchable-select" ? (
            <CommandInput
              value={asyncInput?.search}
              onValueChange={asyncInput?.onSearchChange}
              placeholder={`Search ${filterOption.label.toLowerCase()}...`}
            />
          ) : null}
          <CommandList>
            {!asyncInput?.isItemsLoading && !asyncInput?.isItemsError ? (
              <CommandEmpty>No options found.</CommandEmpty>
            ) : null}
            {items
              .filter((item) => {
                if (asyncInput?.isItemsLoading || asyncInput?.isItemsError)
                  return false;
                return (
                  !asyncInput?.search?.trim() ||
                  asyncInput.matchingValues?.includes(item.value) ||
                  `${item.label} ${item.searchText ?? ""}`
                    .toLowerCase()
                    .includes(asyncInput.search.trim().toLowerCase())
                );
              })
              .map((item, index, visibleItems) => {
                const isSelected = selectedValues.includes(item.value);
                return (
                  <Fragment key={item.value}>
                    {item.group &&
                      (index === 0 ||
                        visibleItems[index - 1]?.group !== item.group) && (
                        <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                          {item.group}
                        </div>
                      )}
                    <CommandItem
                      key={item.value}
                      value={`${item.label} ${item.searchText ?? ""} ${item.value}`}
                      onSelect={() => {
                        if (isSelected)
                          onRemoveFilter(filterOption.value, item.value);
                        else onAddFilter(filterOption.value, item.value);
                      }}
                    >
                      <Check
                        className={isSelected ? "opacity-100" : "opacity-0"}
                      />
                      {item.displayLabel ?? item.label}
                    </CommandItem>
                  </Fragment>
                );
              })}
            {asyncInput ? <AsyncSelectOptions {...asyncInput} /> : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function TaskQuickFilters({
  filterOptions,
  selectedFilters,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  sortOption,
  onSortChange,
  onDraftFiltersChange,
  showQuickFilters = true,
}: {
  filterOptions: FilterOption[];
  selectedFilters: Partial<Record<string, string[]>>;
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
  onClearFilters: () => void;
  sortOption: TaskSortOption;
  onSortChange: (sortOption: TaskSortOption) => void;
  onDraftFiltersChange?: (filters: Partial<Record<string, string[]>>) => void;
  showQuickFilters?: boolean;
}) {
  const [allFiltersOpen, setAllFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<
    Partial<Record<string, string[]>>
  >({});
  useEffect(() => {
    onDraftFiltersChange?.(allFiltersOpen ? draftFilters : {});
  }, [allFiltersOpen, draftFilters, onDraftFiltersChange]);
  const activeFilterCount = useMemo(
    () =>
      Object.values(selectedFilters).reduce(
        (count, values) => count + (values?.length ?? 0),
        0,
      ),
    [selectedFilters],
  );
  const activeFilterSummary = useMemo(
    () =>
      Object.entries(selectedFilters)
        .flatMap(([key, values]) => {
          const filterOption = filterOptions.find(
            (option) =>
              option.value === key ||
              (option.input.type === "date" &&
                option.input.conditionOptions?.some(
                  (condition) => condition.value === key,
                )),
          );
          const label = filterOption?.label ?? key;
          const items =
            filterOption?.input.type === "select" ||
            filterOption?.input.type === "searchable-select"
              ? filterOption.input.items
              : [];

          return (values ?? []).map((value) => {
            const itemLabel = items.find((item) => item.value === value)?.label;
            return `${label}: ${itemLabel ?? value}`;
          });
        })
        .join("\n"),
    [filterOptions, selectedFilters],
  );
  useEffect(() => {
    if (allFiltersOpen) setDraftFilters(selectedFilters);
  }, [allFiltersOpen, selectedFilters]);
  const quickFilters = filterOptions.filter((option) =>
    ["assigneeId", "priority"].includes(option.value),
  );
  const singleValueFilterKeys = useMemo(
    () =>
      new Set(
        filterOptions.flatMap((option) =>
          option.input.type === "date"
            ? (option.input.conditionOptions ?? []).map(
                (condition) => condition.value,
              )
            : [],
        ),
      ),
    [filterOptions],
  );
  const selectedSortLabel =
    taskSortOptions.find((option) => option.value === sortOption)?.label ??
    "Sort";
  const addDraftFilter = (key: string, value: string) => {
    setDraftFilters((previous) => ({
      ...previous,
      [key]: singleValueFilterKeys.has(key)
        ? [value]
        : [...(previous[key] ?? []), value],
    }));
  };
  const removeDraftFilter = (key: string, value: string) => {
    setDraftFilters((previous) => {
      const nextValues = (previous[key] ?? []).filter((item) => item !== value);
      if (nextValues.length) return { ...previous, [key]: nextValues };
      return Object.fromEntries(
        Object.entries(previous).filter(([filterKey]) => filterKey !== key),
      );
    });
  };
  const applyDraftFilters = () => {
    onClearFilters();
    Object.entries(draftFilters).forEach(([key, values]) => {
      values?.forEach((value) => {
        onAddFilter(key, value);
      });
    });
    setAllFiltersOpen(false);
  };

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {showQuickFilters &&
            quickFilters.map((filterOption) => (
              <QuickFilter
                key={filterOption.value}
                filterOption={filterOption}
                selectedValues={selectedFilters[filterOption.value] ?? []}
                onAddFilter={onAddFilter}
                onRemoveFilter={onRemoveFilter}
              />
            ))}
          <TooltipWrapper message={activeFilterSummary}>
            <div
              className={cn(
                "inline-flex",
                activeFilterCount && "h-8 overflow-hidden rounded-full border",
              )}
            >
              <Button
                type="button"
                size="sm"
                variant={activeFilterCount ? "ghost" : "outline"}
                className={cn(
                  "h-8 px-3 text-xs font-medium",
                  activeFilterCount ? "rounded-none" : "rounded-full",
                )}
                onClick={() => {
                  setAllFiltersOpen(true);
                }}
              >
                <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                All filters
                {activeFilterCount ? (
                  <span className="bg-destructive text-destructive-foreground ml-1 min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
              {activeFilterCount ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 w-8 rounded-none border-l p-0"
                  aria-label="Clear filters"
                  onClick={onClearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </TooltipWrapper>
        </div>
        <Popover open={sortOpen} onOpenChange={setSortOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-8 rounded-full px-3 text-xs font-medium"
            >
              <ArrowUpDown className="mr-1.5 h-3.5 w-3.5" />
              Sort: {selectedSortLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-52 p-0">
            <Command>
              <CommandList>
                {taskSortOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onSortChange(option.value);
                      setSortOpen(false);
                    }}
                  >
                    <Check
                      className={
                        sortOption === option.value
                          ? "opacity-100"
                          : "opacity-0"
                      }
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <AppModal
        open={allFiltersOpen}
        onOpenChange={setAllFiltersOpen}
        title="All filters"
        description="Refine the tasks shown in this list."
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setDraftFilters({});
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              onClick={applyDraftFilters}
            >
              Apply filters
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-6">
          {Object.keys(draftFilters).length ? (
            <FilterSelector
              filterOptions={filterOptions}
              selectedFilters={draftFilters}
              onAddFilter={addDraftFilter}
              onRemoveFilter={removeDraftFilter}
              badgesOnly
            />
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            {filterOptions.map((filterOption) => {
              const selectedValues = draftFilters[filterOption.value] ?? [];
              return (
                <FilterSelector
                  key={filterOption.value}
                  filterOptions={[filterOption]}
                  selectedFilters={
                    selectedValues.length
                      ? { [filterOption.value]: selectedValues }
                      : {}
                  }
                  onAddFilter={addDraftFilter}
                  onRemoveFilter={removeDraftFilter}
                  showHelperText={false}
                  selectStyle={null}
                  fullWidth
                  hidePrimarySelect
                  showActiveFilterBadges={false}
                />
              );
            })}
          </div>
        </div>
      </AppModal>
    </>
  );
}
